import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  Mail, FileText, Loader2, Search, Trash2, Check,
  Plus, X, ClipboardList, Eye, EyeOff, Copy, ExternalLink,
  GripVertical, Upload, Image, ArrowLeft, Pencil, ToggleLeft, ToggleRight,
  Type, AtSign, Phone, AlignLeft, List, CheckSquare, CircleDot,
  File as FileIcon, CalendarDays, Hash, Send
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { themedConfirm, themedPrompt } from '@/lib/themed-dialog';
import { publicUrl } from '../../lib/publicUrl';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const GlassCard = ({ children, className = '', ...props }) => (
  <div className={`bg-[#171A2C]/60 backdrop-blur-xl border border-white/5 shadow-[0_8px_32px_0_rgba(0,0,0,0.36)] rounded-2xl ${className}`} {...props}>{children}</div>
);

const FIELD_TYPE_META = [
  { value: 'text', label: 'Short Text', icon: Type, desc: 'Single line text input' },
  { value: 'email', label: 'Email', icon: AtSign, desc: 'Email address' },
  { value: 'phone', label: 'Phone', icon: Phone, desc: 'Phone number' },
  { value: 'textarea', label: 'Long Text', icon: AlignLeft, desc: 'Multi-line text area' },
  { value: 'select', label: 'Dropdown', icon: List, desc: 'Choose from options' },
  { value: 'checkbox', label: 'Checkbox', icon: CheckSquare, desc: 'Yes/No toggle' },
  { value: 'radio', label: 'Radio Buttons', icon: CircleDot, desc: 'Pick one option' },
  { value: 'file', label: 'File Upload', icon: FileIcon, desc: 'Upload documents or images' },
  { value: 'date', label: 'Date', icon: CalendarDays, desc: 'Date picker' },
  { value: 'number', label: 'Number', icon: Hash, desc: 'Numeric input' },
];

/* ─────────── Full Form Builder ─────────── */
function FormBuilder({ form, onSave, onClose }) {
  const [title, setTitle] = useState(form?.title || 'Untitled Form');
  const [description, setDescription] = useState(form?.description || '');
  const [fields, setFields] = useState(form?.fields || []);
  const [headerImage, setHeaderImage] = useState(form?.header_image_url || '');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showFieldPicker, setShowFieldPicker] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const fileRef = useRef(null);
  const token = localStorage.getItem('token');

  const addField = (type) => {
    const meta = FIELD_TYPE_META.find(f => f.value === type);
    const newField = {
      id: `f-${Date.now()}-${Math.random().toString(36).slice(2,6)}`,
      label: meta?.label || 'New Field',
      type,
      required: false,
      placeholder: '',
      options: (type === 'select' || type === 'radio') ? ['Option 1', 'Option 2'] : [],
    };
    setFields(prev => [...prev, newField]);
    setShowFieldPicker(false);
  };

  const updateField = (id, key, value) => {
    setFields(prev => prev.map(f => f.id === id ? { ...f, [key]: value } : f));
  };

  const removeField = (id) => {
    setFields(prev => prev.filter(f => f.id !== id));
  };

  const moveField = (idx, dir) => {
    const arr = [...fields];
    const swap = idx + dir;
    if (swap < 0 || swap >= arr.length) return;
    [arr[idx], arr[swap]] = [arr[swap], arr[idx]];
    setFields(arr);
  };

  const handleHeaderUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(`${API}/media/upload?category=forms`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      setHeaderImage(process.env.REACT_APP_BACKEND_URL + res.data.url);
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); }
  };

  const handleSave = async () => {
    if (!title.trim()) { toast.error('Form needs a title'); return; }
    if (fields.length === 0) { toast.error('Add at least one field'); return; }
    setSaving(true);
    try {
      const payload = { title, description, fields, header_image_url: headerImage };
      await onSave(payload, form?.id);
      toast.success(form?.id ? 'Form updated!' : 'Form created!');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  /* ── Preview Render ── */
  if (previewMode) {
    return (
      <div className="space-y-5" data-testid="form-preview-mode">
        <div className="flex items-center justify-between">
          <button onClick={() => setPreviewMode(false)} className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors" data-testid="exit-preview-btn">
            <ArrowLeft size={16} /> Back to Editor
          </button>
          <span className="text-xs text-slate-500 uppercase tracking-wider">Live Preview</span>
        </div>
        <div className="max-w-xl mx-auto rounded-2xl overflow-hidden border border-white/10 bg-white shadow-2xl">
          {headerImage && <img src={headerImage} alt="" className="w-full h-40 object-cover" />}
          <div className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-1">{title}</h2>
            {description && <p className="text-gray-500 mb-6">{description}</p>}
            <div className="space-y-5">
              {fields.map(field => (
                <div key={field.id}>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    {field.label} {field.required && <span className="text-red-500">*</span>}
                  </label>
                  {field.type === 'textarea' ? (
                    <textarea rows={3} placeholder={field.placeholder} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg bg-gray-50 text-sm" disabled />
                  ) : field.type === 'select' ? (
                    <select className="w-full px-4 py-2.5 border border-gray-200 rounded-lg bg-gray-50 text-sm" disabled>
                      <option>Select...</option>
                      {(field.options || []).map((o, i) => <option key={i}>{o}</option>)}
                    </select>
                  ) : field.type === 'radio' ? (
                    <div className="flex flex-wrap gap-3">
                      {(field.options || []).map((o, i) => (
                        <label key={i} className="flex items-center gap-2 text-sm text-gray-700"><input type="radio" disabled /> {o}</label>
                      ))}
                    </div>
                  ) : field.type === 'checkbox' ? (
                    <label className="flex items-center gap-2 text-sm text-gray-700"><input type="checkbox" disabled /> {field.placeholder || field.label}</label>
                  ) : field.type === 'file' ? (
                    <div className="px-4 py-6 border-2 border-dashed border-gray-200 rounded-lg text-center text-sm text-gray-400">Click to upload a file</div>
                  ) : (
                    <input type={field.type} placeholder={field.placeholder} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg bg-gray-50 text-sm" disabled />
                  )}
                </div>
              ))}
            </div>
            <button className="w-full mt-6 py-3 bg-[#8B5CF6] text-white font-medium rounded-lg flex items-center justify-center gap-2" disabled>
              <Send size={16} /> Submit
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ── Editor ── */
  return (
    <div className="space-y-5" data-testid="form-builder">
      <div className="flex items-center justify-between">
        <button onClick={onClose} className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors" data-testid="form-builder-back-btn">
          <ArrowLeft size={16} /> Back to Forms
        </button>
        <div className="flex items-center gap-2">
          <button onClick={() => setPreviewMode(true)} className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium bg-white/5 border border-white/10 text-slate-300 hover:text-white hover:border-white/20 transition-colors" data-testid="preview-form-btn">
            <Eye size={14} /> Preview
          </button>
          <button onClick={handleSave} disabled={saving} className="flex items-center gap-1.5 px-5 py-2 rounded-xl text-sm font-medium bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white shadow-[0_0_12px_rgba(139,92,246,0.3)] hover:shadow-[0_0_20px_rgba(139,92,246,0.5)] disabled:opacity-50 transition-all" data-testid="save-form-btn">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} {saving ? 'Saving...' : form?.id ? 'Update Form' : 'Create Form'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Left: Form details & fields */}
        <div className="lg:col-span-2 space-y-4">
          {/* Header Image */}
          <GlassCard className="p-5">
            {headerImage ? (
              <div className="relative group mb-4">
                <img src={headerImage} alt="" className="w-full h-36 object-cover rounded-xl" />
                <button onClick={() => setHeaderImage('')} className="absolute top-2 right-2 p-1.5 bg-black/60 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"><X size={14} className="text-white" /></button>
              </div>
            ) : null}
            <div className="space-y-3">
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Form title..."
                className="bg-transparent border-0 text-white text-xl font-bold placeholder:text-slate-600 px-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                data-testid="form-title-input"
              />
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Add a description (optional)"
                rows={2}
                className="bg-transparent border-0 text-slate-400 text-sm placeholder:text-slate-600 px-0 resize-none focus-visible:ring-0 focus-visible:ring-offset-0"
                data-testid="form-description-input"
              />
              <div className="flex gap-2">
                <button onClick={() => fileRef.current?.click()} disabled={uploading} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs bg-white/5 border border-white/10 text-slate-400 hover:text-white hover:border-white/20 transition-colors" data-testid="form-header-image-btn">
                  {uploading ? <Loader2 size={12} className="animate-spin" /> : <Image size={12} />} {uploading ? 'Uploading...' : headerImage ? 'Change Image' : 'Add Header Image'}
                </button>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleHeaderUpload(e.target.files?.[0])} />
              </div>
            </div>
          </GlassCard>

          {/* Fields */}
          <div className="space-y-2">
            {fields.map((field, idx) => {
              const meta = FIELD_TYPE_META.find(f => f.value === field.type);
              const Icon = meta?.icon || Type;
              return (
                <GlassCard key={field.id} className="p-4" data-testid={`form-field-${field.id}`}>
                  <div className="flex items-start gap-3">
                    {/* Drag handle + reorder */}
                    <div className="flex flex-col items-center gap-0.5 pt-1">
                      <button onClick={() => moveField(idx, -1)} disabled={idx === 0} className="text-slate-600 hover:text-white disabled:opacity-20 transition-colors p-0.5">
                        <svg width="12" height="7" viewBox="0 0 12 7"><path d="M1 6L6 1L11 6" stroke="currentColor" strokeWidth="1.5" fill="none"/></svg>
                      </button>
                      <GripVertical size={14} className="text-slate-600" />
                      <button onClick={() => moveField(idx, 1)} disabled={idx === fields.length - 1} className="text-slate-600 hover:text-white disabled:opacity-20 transition-colors p-0.5">
                        <svg width="12" height="7" viewBox="0 0 12 7"><path d="M1 1L6 6L11 1" stroke="currentColor" strokeWidth="1.5" fill="none"/></svg>
                      </button>
                    </div>

                    {/* Field config */}
                    <div className="flex-1 space-y-2.5">
                      <div className="flex items-center gap-2">
                        <div className="p-1.5 rounded-lg bg-[#8B5CF6]/10"><Icon size={12} className="text-[#A78BFA]" /></div>
                        <Input
                          value={field.label}
                          onChange={(e) => updateField(field.id, 'label', e.target.value)}
                          className="bg-white/5 border-white/10 text-white text-sm h-8 rounded-lg flex-1"
                          data-testid={`field-label-${field.id}`}
                        />
                        <select
                          value={field.type}
                          onChange={(e) => updateField(field.id, 'type', e.target.value)}
                          className="bg-[#1E1E3A] border border-white/10 text-slate-300 text-xs h-8 rounded-lg px-2"
                          data-testid={`field-type-${field.id}`}
                        >
                          {FIELD_TYPE_META.map(ft => <option key={ft.value} value={ft.value}>{ft.label}</option>)}
                        </select>
                      </div>

                      {/* Placeholder (for text-like fields) */}
                      {['text', 'email', 'phone', 'textarea', 'number'].includes(field.type) && (
                        <Input
                          value={field.placeholder || ''}
                          onChange={(e) => updateField(field.id, 'placeholder', e.target.value)}
                          placeholder="Placeholder text..."
                          className="bg-white/[0.03] border-white/5 text-slate-400 text-xs h-7 rounded-lg"
                        />
                      )}

                      {/* Options (for select/radio) */}
                      {(field.type === 'select' || field.type === 'radio') && (
                        <div className="space-y-1.5">
                          {(field.options || []).map((opt, oi) => (
                            <div key={oi} className="flex items-center gap-1.5">
                              <span className="text-[10px] text-slate-600 w-4">{oi + 1}.</span>
                              <Input
                                value={opt}
                                onChange={(e) => {
                                  const opts = [...(field.options || [])];
                                  opts[oi] = e.target.value;
                                  updateField(field.id, 'options', opts);
                                }}
                                className="bg-white/[0.03] border-white/5 text-slate-300 text-xs h-6 rounded flex-1"
                              />
                              <button onClick={() => updateField(field.id, 'options', (field.options || []).filter((_, i) => i !== oi))} className="text-slate-600 hover:text-red-400 p-0.5"><X size={10} /></button>
                            </div>
                          ))}
                          <button
                            onClick={() => updateField(field.id, 'options', [...(field.options || []), `Option ${(field.options?.length || 0) + 1}`])}
                            className="text-[10px] text-[#A78BFA] hover:text-white transition-colors"
                          >+ Add option</button>
                        </div>
                      )}

                      {/* Required toggle + delete */}
                      <div className="flex items-center justify-between pt-1">
                        <button
                          onClick={() => updateField(field.id, 'required', !field.required)}
                          className={`flex items-center gap-1.5 text-[10px] font-medium transition-colors ${field.required ? 'text-[#8B5CF6]' : 'text-slate-600 hover:text-slate-400'}`}
                          data-testid={`field-required-${field.id}`}
                        >
                          {field.required ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}
                          {field.required ? 'Required' : 'Optional'}
                        </button>
                        <button onClick={() => removeField(field.id)} className="text-slate-600 hover:text-[#FF6B6B] transition-colors p-1" data-testid={`field-delete-${field.id}`}>
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                  </div>
                </GlassCard>
              );
            })}

            {/* Add Field Button */}
            {showFieldPicker ? (
              <GlassCard className="p-4" data-testid="field-picker">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-medium text-white">Add a Field</p>
                  <button onClick={() => setShowFieldPicker(false)} className="text-slate-500 hover:text-white"><X size={14} /></button>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {FIELD_TYPE_META.map(ft => {
                    const Icon = ft.icon;
                    return (
                      <button
                        key={ft.value}
                        onClick={() => addField(ft.value)}
                        className="flex items-center gap-2.5 p-3 rounded-xl bg-white/[0.03] border border-white/5 hover:border-[#8B5CF6]/30 hover:bg-[#8B5CF6]/5 text-left transition-all group"
                        data-testid={`add-field-${ft.value}`}
                      >
                        <div className="p-1.5 rounded-lg bg-white/5 group-hover:bg-[#8B5CF6]/20 transition-colors">
                          <Icon size={14} className="text-slate-400 group-hover:text-[#A78BFA] transition-colors" />
                        </div>
                        <div>
                          <p className="text-xs font-medium text-white">{ft.label}</p>
                          <p className="text-[9px] text-slate-500">{ft.desc}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </GlassCard>
            ) : (
              <button
                onClick={() => setShowFieldPicker(true)}
                className="w-full py-3 rounded-2xl border-2 border-dashed border-white/10 text-sm font-medium text-slate-500 hover:text-[#A78BFA] hover:border-[#8B5CF6]/30 transition-all flex items-center justify-center gap-2"
                data-testid="add-field-trigger"
              >
                <Plus size={16} /> Add Field
              </button>
            )}
          </div>
        </div>

        {/* Right: Mini live preview */}
        <div className="hidden lg:block">
          <div className="sticky top-4">
            <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Live Preview</p>
            <div className="rounded-2xl overflow-hidden border border-white/10 bg-white shadow-xl" style={{ fontSize: '11px' }}>
              {headerImage && <img src={headerImage} alt="" className="w-full h-20 object-cover" />}
              <div className="p-4">
                <p className="font-bold text-gray-900 mb-0.5" style={{ fontSize: 13 }}>{title || 'Untitled Form'}</p>
                {description && <p className="text-gray-400 mb-3" style={{ fontSize: 9 }}>{description}</p>}
                <div className="space-y-2">
                  {fields.map(f => (
                    <div key={f.id}>
                      <p className="font-medium text-gray-600 mb-0.5" style={{ fontSize: 9 }}>{f.label}{f.required ? ' *' : ''}</p>
                      {f.type === 'textarea' ? <div className="h-8 bg-gray-100 rounded border border-gray-200" /> :
                       f.type === 'select' ? <div className="h-5 bg-gray-100 rounded border border-gray-200 flex items-center px-1.5" style={{ fontSize: 8 }}>Select...</div> :
                       f.type === 'checkbox' ? <div className="flex items-center gap-1"><div className="w-3 h-3 border border-gray-300 rounded" /><span style={{ fontSize: 8 }}>{f.placeholder || f.label}</span></div> :
                       f.type === 'radio' ? <div className="flex gap-2">{(f.options || []).slice(0, 3).map((o, i) => <div key={i} className="flex items-center gap-0.5"><div className="w-2.5 h-2.5 border border-gray-300 rounded-full" /><span style={{ fontSize: 8 }}>{o}</span></div>)}</div> :
                       f.type === 'file' ? <div className="h-8 bg-gray-50 rounded border-2 border-dashed border-gray-200 flex items-center justify-center" style={{ fontSize: 8 }}>Upload file</div> :
                       <div className="h-5 bg-gray-100 rounded border border-gray-200" />}
                    </div>
                  ))}
                </div>
                {fields.length > 0 && <div className="mt-3 h-6 bg-[#8B5CF6] rounded text-white flex items-center justify-center" style={{ fontSize: 9 }}>Submit</div>}
                {fields.length === 0 && <p className="text-gray-300 text-center py-4" style={{ fontSize: 9 }}>Add fields to see preview</p>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─────────── Main Messages Page ─────────── */
export default function DashboardMessages() {
  const [params] = useSearchParams();
  const initialTab = ['submissions', 'forms', 'signups'].includes(params.get('tab')) ? params.get('tab') : 'submissions';
  const [tab, setTab] = useState(initialTab);
  const [submissions, setSubmissions] = useState([]);
  const [signups, setSignups] = useState([]);
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedSub, setSelectedSub] = useState(null);
  const [editingForm, setEditingForm] = useState(null); // null = list, 'new' = builder, {form} = editing

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [subsRes, signupsRes, formsRes] = await Promise.all([
        axios.get(`${API}/form-submissions`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/signups`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/forms`, { headers }).catch(() => ({ data: [] })),
      ]);
      setSubmissions(subsRes.data || []);
      setSignups(signupsRes.data || []);
      setForms(formsRes.data || []);
    } finally { setLoading(false); }
  };

  const markAsRead = async (sub) => {
    if (sub.is_read) return;
    try {
      await axios.put(`${API}/form-submissions/${sub.id}/read`, {}, { headers });
      setSubmissions(prev => prev.map(s => s.id === sub.id ? { ...s, is_read: true } : s));
    } catch {}
  };

  const deleteSubmission = async (id) => {
    if (!(await themedConfirm('Delete this submission?'))) return;
    try {
      await axios.delete(`${API}/form-submissions/${id}`, { headers });
      setSubmissions(prev => prev.filter(s => s.id !== id));
      if (selectedSub?.id === id) setSelectedSub(null);
      toast.success('Deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const handleSaveForm = async (payload, existingId) => {
    if (existingId) {
      await axios.put(`${API}/forms/${existingId}`, payload, { headers });
      setForms(prev => prev.map(f => f.id === existingId ? { ...f, ...payload } : f));
    } else {
      const res = await axios.post(`${API}/forms`, payload, { headers });
      setForms(prev => [res.data, ...prev]);
    }
    setEditingForm(null);
  };

  const toggleFormActive = async (formId, currentActive) => {
    try {
      await axios.put(`${API}/forms/${formId}`, { is_active: !currentActive }, { headers });
      setForms(prev => prev.map(f => f.id === formId ? { ...f, is_active: !currentActive } : f));
      toast.success(!currentActive ? 'Form activated' : 'Form deactivated');
    } catch { toast.error('Failed to update'); }
  };

  const deleteForm = async (id) => {
    if (!(await themedConfirm('Delete this form and all its submissions?'))) return;
    try {
      await axios.delete(`${API}/forms/${id}`, { headers });
      setForms(prev => prev.filter(f => f.id !== id));
      setSubmissions(prev => prev.filter(s => s.form_id !== id));
      toast.success('Form deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const copyFormLink = (formId) => {
    // Iter 198i — Public share links MUST resolve to the customer-facing
    // entreprenatives.com host, never to the internal preview origin.
    const link = publicUrl(`/api/public/forms/${formId}`);
    navigator.clipboard.writeText(link);
    toast.success('Form link copied!');
  };

  const unreadCount = submissions.filter(s => !s.is_read).length;
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

  const filteredSubs = submissions.filter(s => {
    if (!search) return true;
    const q = search.toLowerCase();
    return s.form_title?.toLowerCase().includes(q) || JSON.stringify(s.data).toLowerCase().includes(q);
  });

  const filteredSignups = signups.filter(s => {
    if (!search) return true;
    return s.email?.toLowerCase().includes(search.toLowerCase());
  });

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 size={32} className="animate-spin text-[#8B5CF6]" /></div>;

  /* ── Form Builder View ── */
  if (editingForm !== null) {
    return (
      <div className="space-y-6" data-testid="messages-page">
        <FormBuilder
          form={editingForm === 'new' ? null : editingForm}
          onSave={handleSaveForm}
          onClose={() => setEditingForm(null)}
        />
      </div>
    );
  }

  /* ── Main List View ── */
  return (
    <div className="space-y-6" data-testid="messages-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Messages</h1>
          <p className="text-sm text-slate-400 mt-1">Form submissions, forms, and subscriber signups</p>
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-2 flex-wrap" data-testid="messages-tabs">
        {[
          { id: 'submissions', label: 'Form Submissions', icon: ClipboardList, badge: unreadCount },
          { id: 'forms', label: 'My Forms', icon: FileText, badge: forms.length },
          { id: 'signups', label: 'Email Signups', icon: Mail, badge: 0 },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${tab === t.id ? 'bg-gradient-to-r from-[#8B5CF6]/20 to-[#3B82F6]/10 text-white border border-[#8B5CF6]/30' : 'text-slate-400 hover:text-white hover:bg-white/5 border border-transparent'}`}
            data-testid={`tab-${t.id}`}
          >
            <t.icon size={16} /> {t.label}
            {t.badge > 0 && <span className="ml-1 px-1.5 py-0.5 text-[10px] rounded-full bg-[#8B5CF6] text-white font-bold">{t.badge}</span>}
          </button>
        ))}
      </div>

      {/* Search (not for forms tab) */}
      {tab !== 'forms' && (
        <div className="relative max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={tab === 'submissions' ? 'Search submissions...' : 'Search signups...'} className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-[#64748B] rounded-xl" data-testid="messages-search" />
        </div>
      )}

      {/* ── Form Submissions Tab ── */}
      {tab === 'submissions' && (
        <div className="space-y-3" data-testid="submissions-list">
          {filteredSubs.length === 0 ? (
            <GlassCard className="p-12 text-center">
              <ClipboardList size={48} className="mx-auto text-slate-600 mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">No form submissions yet</h3>
              <p className="text-sm text-slate-400">When visitors fill out your forms, their responses will appear here.</p>
            </GlassCard>
          ) : (
            filteredSubs.map(sub => (
              <GlassCard
                key={sub.id}
                className={`p-4 cursor-pointer hover:border-[#8B5CF6]/30 transition-all ${!sub.is_read ? 'border-l-2 border-l-[#8B5CF6]' : ''}`}
                onClick={() => { setSelectedSub(sub); markAsRead(sub); }}
                data-testid={`submission-${sub.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`p-2 rounded-xl ${!sub.is_read ? 'bg-[#8B5CF6]/20' : 'bg-white/5'}`}>
                      <FileText size={16} className={!sub.is_read ? 'text-[#A78BFA]' : 'text-slate-500'} />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className={`text-sm font-medium truncate ${!sub.is_read ? 'text-white' : 'text-slate-300'}`}>{sub.form_title || 'Form Submission'}</p>
                        {!sub.is_read && <div className="w-2 h-2 rounded-full bg-[#8B5CF6] shrink-0" />}
                      </div>
                      <p className="text-xs text-slate-500 truncate mt-0.5">
                        {Object.entries(sub.data || {}).slice(0, 2).map(([k, v]) => `${k}: ${typeof v === 'string' ? v.substring(0, 30) : v}`).join(' · ')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0 ml-4">
                    <span className="text-[10px] text-slate-500">{formatDate(sub.submitted_at)}</span>
                    <button onClick={(e) => { e.stopPropagation(); deleteSubmission(sub.id); }} className="p-1.5 text-slate-500 hover:text-[#FF6B6B] rounded-lg hover:bg-red-500/10 transition-colors"><Trash2 size={14} /></button>
                  </div>
                </div>
              </GlassCard>
            ))
          )}
        </div>
      )}

      {/* ── My Forms Tab ── */}
      {tab === 'forms' && (
        <div className="space-y-4" data-testid="forms-list">
          <button
            onClick={() => setEditingForm('new')}
            className="w-full py-4 rounded-2xl border-2 border-dashed border-[#8B5CF6]/30 text-sm font-medium text-[#A78BFA] hover:bg-[#8B5CF6]/5 hover:border-[#8B5CF6]/50 transition-all flex items-center justify-center gap-2"
            data-testid="create-new-form-btn"
          >
            <Plus size={18} /> Create New Form
          </button>

          {forms.length === 0 ? (
            <GlassCard className="p-12 text-center">
              <FileText size={48} className="mx-auto text-slate-600 mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">No forms yet</h3>
              <p className="text-sm text-slate-400 max-w-sm mx-auto">Create your first form to start collecting inquiries, bookings, and feedback from your visitors.</p>
            </GlassCard>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {forms.map(form => (
                <GlassCard key={form.id} className="p-5 group hover:border-[#8B5CF6]/20 transition-all" data-testid={`form-card-${form.id}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-white truncate">{form.title}</h3>
                        <span className={`px-1.5 py-0.5 text-[9px] rounded-full font-medium ${form.is_active ? 'bg-[#00CC99]/10 text-[#00CC99] border border-[#00CC99]/20' : 'bg-white/5 text-slate-500 border border-white/10'}`}>
                          {form.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      {form.description && <p className="text-xs text-slate-500 truncate mt-0.5">{form.description}</p>}
                    </div>
                  </div>

                  <div className="flex items-center gap-4 mb-4 text-xs text-slate-500">
                    <span>{form.fields?.length || 0} fields</span>
                    <span>{form.submission_count || 0} responses</span>
                    <span>{formatDate(form.created_at).split(',')[0]}</span>
                  </div>

                  <div className="flex items-center gap-2">
                    <button onClick={() => setEditingForm(form)} className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-medium bg-white/5 border border-white/10 text-slate-300 hover:text-white hover:border-white/20 transition-colors" data-testid={`edit-form-${form.id}`}>
                      <Pencil size={12} /> Edit
                    </button>
                    <button onClick={() => toggleFormActive(form.id, form.is_active)} className="flex items-center justify-center gap-1.5 py-2 px-3 rounded-xl text-xs font-medium bg-white/5 border border-white/10 text-slate-300 hover:text-white hover:border-white/20 transition-colors" data-testid={`toggle-form-${form.id}`}>
                      {form.is_active ? <EyeOff size={12} /> : <Eye size={12} />}
                    </button>
                    <button onClick={() => copyFormLink(form.id)} className="flex items-center justify-center gap-1.5 py-2 px-3 rounded-xl text-xs font-medium bg-white/5 border border-white/10 text-slate-300 hover:text-white hover:border-white/20 transition-colors" data-testid={`copy-form-link-${form.id}`}>
                      <Copy size={12} />
                    </button>
                    <button onClick={() => deleteForm(form.id)} className="flex items-center justify-center py-2 px-3 rounded-xl text-xs font-medium bg-red-500/5 border border-red-500/10 text-red-400/70 hover:text-[#FF6B6B] hover:border-red-500/20 transition-colors" data-testid={`delete-form-${form.id}`}>
                      <Trash2 size={12} />
                    </button>
                  </div>
                </GlassCard>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Signups Tab ── */}
      {tab === 'signups' && (
        <div className="space-y-3" data-testid="signups-list">
          {filteredSignups.length === 0 ? (
            <GlassCard className="p-12 text-center">
              <Mail size={48} className="mx-auto text-slate-600 mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">No signups yet</h3>
              <p className="text-sm text-slate-400">Newsletter subscribers from your signup block will appear here.</p>
            </GlassCard>
          ) : (
            <GlassCard>
              <table className="w-full text-left text-sm">
                <thead><tr className="text-xs uppercase tracking-wider text-slate-400 border-b border-white/10">
                  <th className="px-6 py-3 font-medium">Email</th>
                  <th className="px-6 py-3 font-medium">Date</th>
                </tr></thead>
                <tbody>
                  {filteredSignups.map((s, i) => (
                    <tr key={i} className="border-b border-white/5">
                      <td className="px-6 py-3.5 text-white">{s.email}</td>
                      <td className="px-6 py-3.5 text-slate-400 text-xs">{formatDate(s.created_at || s.signed_up_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </GlassCard>
          )}
        </div>
      )}

      {/* Submission Detail Dialog */}
      <Dialog open={!!selectedSub} onOpenChange={() => setSelectedSub(null)}>
        <DialogContent className="bg-[#0E0E23] border-white/10 text-white max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg flex items-center gap-2">
              <FileText size={18} className="text-[#A78BFA]" />
              {selectedSub?.form_title || 'Form Submission'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <p className="text-xs text-slate-500">{formatDate(selectedSub?.submitted_at)}</p>
            <div className="space-y-3">
              {selectedSub && Object.entries(selectedSub.data || {}).map(([key, value]) => (
                <div key={key} className="p-3 rounded-xl bg-white/5 border border-white/5">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{key}</p>
                  {typeof value === 'string' && (value.startsWith('http://') || value.startsWith('https://')) ? (
                    value.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                      <img src={value} alt={key} className="max-w-full h-32 object-cover rounded-lg" />
                    ) : (
                      <a href={value} target="_blank" rel="noopener noreferrer" className="text-sm text-[#A78BFA] hover:underline break-all">{value}</a>
                    )
                  ) : (
                    <p className="text-sm text-white break-words whitespace-pre-wrap">{String(value)}</p>
                  )}
                </div>
              ))}
            </div>
            <div className="flex gap-2 pt-2">
              <button onClick={() => setSelectedSub(null)} className="flex-1 py-2 rounded-xl text-xs font-medium bg-white/5 border border-white/10 text-slate-300">Close</button>
              <button onClick={() => { deleteSubmission(selectedSub.id); }} className="py-2 px-4 rounded-xl text-xs font-medium bg-red-500/10 border border-red-500/20 text-[#FF6B6B] flex items-center gap-1.5"><Trash2 size={12} /> Delete</button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
